import { useRef, useState, useCallback } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import CloseIcon from '@mui/icons-material/Close'
import { useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { listingService } from '../services/ListingService'
import { PhotoMetadata } from '../types/listing_onboarding'

const MAX_PHOTOS = 5

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #ccc',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: '#fafafa',
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}))

type Props = {
  pendingListingId: string
  photos: PhotoMetadata[]
  onPhotosChange: (photos: PhotoMetadata[]) => void
  isEnsuringId?: boolean
  ensureIdError?: string | null
}

export default function LandlordPhotoPicker({
  pendingListingId,
  photos,
  onPhotosChange,
  isEnsuringId = false,
  ensureIdError = null,
}: Props) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useMediaQuery('(max-width:600px)')

  const canAddMore = photos.length < MAX_PHOTOS

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!pendingListingId) return
      const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'))
      if (files.length === 0) return
      const room = MAX_PHOTOS - photos.length
      const toUpload = files.slice(0, room)
      if (files.length > room) {
        setUploadError(`You can add up to ${MAX_PHOTOS} photos. Extra files were not added.`)
      } else {
        setUploadError(null)
      }
      setIsUploading(true)
      try {
        const results = await Promise.all(toUpload.map((file) => listingService.uploadPhoto(pendingListingId, file)))
        onPhotosChange([...photos, ...results])
      } catch (err) {
        console.error(err)
        setUploadError('Upload failed. Please try again.')
      } finally {
        setIsUploading(false)
        e.target.value = ''
      }
    },
    [pendingListingId, photos, onPhotosChange]
  )

  const remove = async (photoId: string) => {
    if (!pendingListingId) return
    try {
      await listingService.deletePendingPhoto(pendingListingId, photoId)
      onPhotosChange(photos.filter((p) => p.photoId !== photoId))
      setUploadError(null)
    } catch (err) {
      console.error(err)
      setUploadError('Could not remove photo.')
    }
  }

  const setFeatured = async (photoId: string) => {
    if (!pendingListingId) return
    try {
      await listingService.setFeaturedPhoto(pendingListingId, photoId)
      onPhotosChange(
        photos.map((p) => ({
          ...p,
          isFeatured: p.photoId === photoId,
        }))
      )
    } catch (err) {
      console.error(err)
    }
  }

  if (isEnsuringId) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, justifyContent: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          Preparing photo upload…
        </Typography>
      </Box>
    )
  }

  if (ensureIdError) {
    return (
      <Typography color="error" variant="body2" sx={{ py: 2 }}>
        {ensureIdError}
      </Typography>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Tap the star to set your feature photo. Tap the X to remove a photo.
      </Typography>
      {uploadError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {uploadError}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleFileChange}
      />

      {canAddMore && pendingListingId && (
        <UploadBox
          onClick={() => !isUploading && inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            if (isUploading) return
            const dt = e.dataTransfer.files
            const synthetic = { target: { files: dt } } as React.ChangeEvent<HTMLInputElement>
            void handleFileChange(synthetic)
          }}
          sx={{ opacity: isUploading ? 0.6 : 1, pointerEvents: isUploading ? 'none' : 'auto' }}
        >
          <CloudUploadIcon sx={{ fontSize: 36, color: 'grey.600', mb: 1 }} />
          <Typography variant="body1" fontWeight={600}>
            {isMobile ? 'Tap to upload' : 'Drag and drop or click to upload'}
          </Typography>
          {isUploading && <CircularProgress size={20} sx={{ mt: 1 }} />}
        </UploadBox>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 1.5,
          mt: 2,
        }}
      >
        {photos.map((p) => (
          <Box
            key={p.photoId}
            sx={{
              position: 'relative',
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid #e0e0e0',
              aspectRatio: '1',
            }}
          >
            <Box
              component="img"
              src={p.previewUrl}
              alt="Listing"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <IconButton
              size="small"
              onClick={() => setFeatured(p.photoId)}
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                bgcolor: 'rgba(255,255,255,0.9)',
                p: 0.5,
              }}
              aria-label={p.isFeatured ? 'Feature photo' : 'Set as feature photo'}
            >
              {p.isFeatured ? <StarIcon sx={{ color: '#fbbf24' }} /> : <StarBorderIcon />}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => remove(p.photoId)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(255,255,255,0.9)',
                p: 0.5,
              }}
              aria-label="Remove photo"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
